import { http } from "@shared/utils/request";
import type { ListResult } from "@shared/types/list";
import type { PermissionTreeNode, RoleAssignableUser, RoleManagementApi, RoleMemberRow, RoleOptionItem, RoleRow } from "@app/views/system/role/types";

interface RoleListApiData {
    items: RoleRow[];
}

interface RoleOptionsApiData {
    items: RoleOptionItem[];
}

interface RoleMembersApiData {
    items: RoleMemberRow[];
    total: number;
}

interface RoleAssignableApiData {
    items: RoleAssignableUser[];
}

interface PermissionTreeApiData {
    items: PermissionTreeNode[];
}

/** 本企业全部角色（不分页） */
export function fetchRoleList(keyword = ""): Promise<RoleRow[]> {
    return http.get<RoleListApiData>("/customer/role/list", { keyword: keyword || undefined }).then(res => res.items || []);
}

/** 角色下拉选项 */
export function fetchRoleOptions(): Promise<RoleOptionItem[]> {
    return http.get<RoleOptionsApiData>("/customer/role/options").then(res => res.items || []);
}

/** 新增角色 → 新角色ID */
export function addRole(payload: { name: string; description?: string }): Promise<{ id: string }> {
    return http.post<{ id: string }>("/customer/role/add", payload).then(res => ({ id: res.id }));
}

/** 编辑角色（名称/描述；内置同样允许） */
export function editRole(payload: { id: string; name: string; description?: string }): Promise<void> {
    return http.post<void>("/customer/role/edit", payload);
}

/** 删除角色（内置/有成员不可删） */
export function deleteRole(id: string): Promise<void> {
    return http.post<void>("/customer/role/delete", { id });
}

/** 当前端权限树 */
export function fetchPermissionTree(): Promise<PermissionTreeNode[]> {
    return http.get<PermissionTreeApiData>("/customer/permissions/tree").then(res => res.items ?? []);
}

/** 全量保存角色权限 */
export function saveRolePermissions(roleId: string, permissionIds: string[]): Promise<void> {
    return http.post<void>("/customer/role/savePermissions", { roleId, permissionIds });
}

/** 内置角色：按开户基准恢复当前端权限 */
export function restoreRolePermissions(roleId: string): Promise<void> {
    return http.post<void>("/customer/role/restorePermissions", { roleId });
}

/** 角色成员分页 */
export function fetchRoleMembers(roleId: string, query: { page: number; limit: number; keyword: string }): Promise<ListResult<RoleMemberRow>> {
    return http
        .get<RoleMembersApiData>("/customer/role/members", {
            roleId,
            page: query.page,
            perPage: query.limit,
            keyword: query.keyword || undefined,
        })
        .then(res => ({ list: res.items || [], total: res.total ?? 0 }));
}

/** 本企业未绑定该角色的用户 */
export function fetchAssignableUsers(roleId: string, keyword = ""): Promise<RoleAssignableUser[]> {
    return http.get<RoleAssignableApiData>("/customer/role/assignableUsers", { roleId: roleId || undefined, keyword: keyword || undefined }).then(res => res.items || []);
}

/** 批量绑定成员（已绑定自动跳过） */
export function addRoleMembers(roleId: string, userIds: string[]): Promise<void> {
    return http.post<void>("/customer/role/addMembers", { roleId, userIds });
}

/** 批量解绑成员（幂等） */
export function removeRoleMembers(roleId: string, userIds: string[]): Promise<void> {
    return http.post<void>("/customer/role/removeMembers", { roleId, userIds });
}

/** 角色管理页注入包 */
export const roleManagementApi: RoleManagementApi = {
    fetchList: fetchRoleList,
    fetchOptions: fetchRoleOptions,
    add: addRole,
    edit: editRole,
    delete: deleteRole,
    fetchPermissionTree,
    savePermissions: saveRolePermissions,
    restorePermissions: restoreRolePermissions,
    fetchMembers: fetchRoleMembers,
    fetchAssignableUsers,
    addMembers: addRoleMembers,
    removeMembers: removeRoleMembers,
};
